/**
 * @file Logger tiền tố theo domain (`boot`, `auth`, `api`…), mức log theo `import.meta.env.PROD`.
 *
 * **Hiệu năng & PROD trace:** `debug` thường tắt trên PROD (`minLevel: info`) — **không** truyền object lớn vào `debug` mà không bọc `import.meta.env.DEV` (vì object vẫn được tạo trước khi gọi). Trên PROD, dùng **`info` / `warn` / `error`** với **chuỗi ngắn** hoặc object **nhỏ** (vài field primitive) để vẫn trace được khi hỗ trợ vận hành; chi tiết sâu (URL đầy đủ, dump) chỉ trong **DEV** + `debug`.
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LoggerConfig {
  prefix: string;
  enabled: boolean;
  minLevel: LogLevel;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class Logger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      prefix: config.prefix || "[App]",
      enabled: config.enabled !== false,
      minLevel: config.minLevel || (import.meta.env.PROD ? "info" : "debug"),
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.minLevel];
  }

  private formatMessage(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const prefix = `${this.config.prefix} [${level.toUpperCase()}] ${timestamp}`;
    return data ? `${prefix} ${message}` : `${prefix} ${message}`;
  }

  debug(message: string, data?: any) {
    if (this.shouldLog("debug")) {
      console.debug(this.formatMessage("debug", message), data || "");
    }
  }

  info(message: string, data?: any) {
    if (this.shouldLog("info")) {
      console.info(this.formatMessage("info", message), data || "");
    }
  }

  warn(message: string, data?: any) {
    if (this.shouldLog("warn")) {
      console.warn(this.formatMessage("warn", message), data || "");
    }
  }

  error(message: string, error?: any) {
    if (this.shouldLog("error")) {
      console.error(this.formatMessage("error", message), error || "");

      // TODO: Gửi error lên monitoring service (Sentry, LogRocket, etc.)
      // if (import.meta.env.PROD) {
      //   Sentry.captureException(error, { extra: { message } });
      // }
    }
  }

  createChild(prefix: string): Logger {
    return new Logger({
      ...this.config,
      prefix: `${this.config.prefix}${prefix}`,
    });
  }
}

// Export singleton instances
export const bootLogger = new Logger({ prefix: "[Boot]" });
export const logger = new Logger({ prefix: "[App]" });
export const routeLogger = new Logger({ prefix: "[Route]" });
export const authLogger = new Logger({ prefix: "[Auth]" });
export const apiLogger = new Logger({ prefix: "[API]" });
